import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, Upload, MagicWand, Check, X as XIcon } from "@phosphor-icons/react";

/**
 * RosterImageAssign — Upload/capture a photo of a player names list.
 * Claude Vision extracts names + fuzzy-matches to registered users.
 * User confirms each match via dropdown, then bulk-assigns.
 *
 * Props:
 *  - retaId: string
 *  - courtIdx: number
 *  - allPlayers: [{id, name}] (all registered users in system, used as fallback for dropdown)
 *  - onAssigned: () => void (callback after bulk assign succeeds)
 *  - onCancel: () => void
 */
export default function RosterImageAssign({ retaId, courtIdx, allPlayers = [], onAssigned, onCancel }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // [{extracted_name, suggested_user_id, suggested_user_name, confidence, alternatives, chosen_user_id, chosen_name, is_guest}]
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const fileToB64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result || "";
      const b64 = String(dataUrl).split(",")[1] || "";
      resolve({ b64, mime: file.type || "image/jpeg" });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const compress = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((b) => resolve(b ? new File([b], "roster.jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      toast.error("Solo JPEG, PNG o WEBP");
      return;
    }
    setAnalyzing(true);
    try {
      const compressed = await compress(f);
      const { b64, mime } = await fileToB64(compressed);
      const res = await api.post(
        `/retas/multi/${retaId}/courts/${courtIdx}/analyze-roster-image`,
        { image_base64: b64, mime },
        { timeout: 60000 }
      );
      const arr = res.data?.suggestions || [];
      if (!arr.length) {
        toast.error("No se detectaron nombres en la imagen");
        return;
      }
      // Initialize chosen values from suggestions
      const initialized = arr.map((s) => ({
        ...s,
        chosen_user_id: s.suggested_user_id || "",
        chosen_name: s.suggested_user_name || s.extracted_name,
        is_guest: !s.suggested_user_id,
        include: true,
      }));
      setSuggestions(initialized);
      toast.success(`${arr.length} nombre${arr.length !== 1 ? "s" : ""} detectado${arr.length !== 1 ? "s" : ""}. Confirma y asigna.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar imagen");
    } finally {
      setAnalyzing(false);
    }
  };

  const pickFor = (idx, value) => {
    setSuggestions((prev) => {
      const next = [...prev];
      const item = { ...next[idx] };
      if (value === "__guest__") {
        item.chosen_user_id = "";
        item.is_guest = true;
        item.chosen_name = item.extracted_name;
      } else {
        const u = allPlayers.find((p) => p.id === value) || item.alternatives.find((a) => a.id === value);
        if (u) {
          item.chosen_user_id = u.id;
          item.chosen_name = u.name;
          item.is_guest = false;
        }
      }
      next[idx] = item;
      return next;
    });
  };

  const toggleInclude = (idx) => {
    setSuggestions((prev) => prev.map((s, i) => i === idx ? { ...s, include: !s.include } : s));
  };

  const editName = (idx, name) => {
    setSuggestions((prev) => prev.map((s, i) => i === idx ? { ...s, chosen_name: name } : s));
  };

  const submit = async () => {
    if (!suggestions || !suggestions.length) return;
    const toAssign = suggestions.filter((s) => s.include && (s.chosen_user_id || (s.chosen_name || "").trim()));
    if (!toAssign.length) {
      toast.error("Marca al menos un jugador para asignar");
      return;
    }
    setSubmitting(true);
    let ok = 0, fail = 0;
    for (const item of toAssign) {
      const payload = item.chosen_user_id
        ? { user_id: item.chosen_user_id, name: item.chosen_name, is_guest: false }
        : { name: item.chosen_name, is_guest: true };
      try {
        await api.post(`/retas/multi/${retaId}/courts/${courtIdx}/assign`, payload);
        ok++;
      } catch {
        fail++;
      }
    }
    setSubmitting(false);
    if (ok > 0) toast.success(`${ok} jugador${ok !== 1 ? "es" : ""} asignado${ok !== 1 ? "s" : ""}`);
    if (fail > 0) toast.error(`${fail} no se pudieron asignar (quizás duplicados o cupo lleno)`);
    if (ok > 0) onAssigned?.();
  };

  // Step 1: pre-upload (no suggestions yet)
  if (!suggestions) {
    return (
      <div className="space-y-3" data-testid="roster-image-assign">
        <div className="text-[11px] text-gray-400 leading-relaxed">
          Sube o toma una foto de tu lista (papel, captura de WhatsApp, etc.). La IA leerá los nombres y los sugerirá automáticamente.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={analyzing}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
            data-testid="roster-image-camera-btn"
          >
            <Camera size={18} weight="bold" className="mr-2" /> Tomar Foto
          </Button>
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={analyzing}
            variant="outline"
            className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase"
            data-testid="roster-image-upload-btn"
          >
            <Upload size={18} weight="bold" className="mr-2" /> Subir Imagen
          </Button>
        </div>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="roster-image-camera-input" />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="roster-image-file-input" />
        {analyzing && (
          <div className="text-center text-xs text-[#CCFF00] flex items-center justify-center gap-2 py-2" data-testid="roster-analyzing">
            <MagicWand size={16} className="animate-pulse" /> Analizando con IA...
          </div>
        )}
        <Button variant="outline" onClick={onCancel} className="border-white/10 w-full" disabled={analyzing}>Cancelar</Button>
      </div>
    );
  }

  // Step 2: confirm matches
  return (
    <div className="space-y-3" data-testid="roster-image-confirm">
      <div className="text-[11px] text-gray-400">
        Confirma cada coincidencia. Verde = match alto, ámbar = revisar, rojo = no encontrado (se asignará como invitado).
      </div>
      <div className="max-h-[50vh] overflow-y-auto space-y-2 border border-white/5 rounded p-2 bg-black/30">
        {suggestions.map((s, idx) => {
          const conf = s.confidence || 0;
          const color = s.chosen_user_id ? "bg-green-500/10 border-green-500/40" : conf >= 0.5 ? "bg-amber-500/10 border-amber-500/40" : "bg-red-500/10 border-red-500/30";
          return (
            <div key={idx} className={`border rounded p-2 ${color} ${!s.include ? "opacity-40" : ""}`} data-testid={`roster-suggestion-${idx}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => toggleInclude(idx)}
                    className={`shrink-0 w-5 h-5 rounded flex items-center justify-center ${s.include ? "bg-[#CCFF00] text-black" : "bg-white/10 text-white/40 border border-white/20"}`}
                    title={s.include ? "Excluir" : "Incluir"}
                    data-testid={`roster-toggle-${idx}`}
                  >
                    {s.include ? <Check size={12} weight="bold" /> : <XIcon size={12} weight="bold" />}
                  </button>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">Detectado:</span>
                  <span className="text-sm text-white truncate font-mono">{s.extracted_name}</span>
                </div>
                {s.chosen_user_id && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[9px] shrink-0">Registrado</Badge>
                )}
                {!s.chosen_user_id && (
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px] shrink-0">Invitado</Badge>
                )}
              </div>
              <Select value={s.chosen_user_id || "__guest__"} onValueChange={(v) => pickFor(idx, v)}>
                <SelectTrigger className="bg-black border-white/10 h-9 text-xs" data-testid={`roster-select-${idx}`}>
                  <SelectValue placeholder="Asignar a..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__guest__">→ Mantener como invitado</SelectItem>
                  {s.alternatives && s.alternatives.length > 0 && (
                    <>
                      {s.alternatives.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} {a.score >= 0.8 ? "⭐" : a.score >= 0.5 ? "•" : ""}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {allPlayers.length > 0 && (
                    <>
                      <SelectItem value="__separator__" disabled>──── Todos ────</SelectItem>
                      {allPlayers
                        .filter((p) => !s.alternatives?.some((a) => a.id === p.id))
                        .slice(0, 50)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {!s.chosen_user_id && (
                <input
                  type="text"
                  value={s.chosen_name || ""}
                  onChange={(e) => editName(idx, e.target.value)}
                  placeholder="Nombre del invitado"
                  className="w-full mt-1.5 bg-black border border-white/10 rounded px-2 py-1 text-xs text-white"
                  data-testid={`roster-guest-name-${idx}`}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} className="border-white/10 flex-1" disabled={submitting}>
          Cancelar
        </Button>
        <Button onClick={submit} disabled={submitting} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase flex-1" data-testid="roster-submit-btn">
          {submitting ? "Asignando..." : `Asignar ${suggestions.filter(s => s.include).length}`}
        </Button>
      </div>
    </div>
  );
}
